import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { T, BI, Img, SaveBar, Section } from "./fields";

export default function ContentTab() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.get("/admin/content").then((r) => setData(r.data)).catch(() => toast.error("Failed to load content")); }, []);
    if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;

    const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
    const setArr = (key, i, patch) => setData((d) => ({ ...d, [key]: (d[key] || []).map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/content", data);
            toast.success("Content saved");
        } catch {
            toast.error("Save failed");
        }
        setSaving(false);
    };

    return (
        <div className="max-w-3xl space-y-4" data-testid="content-tab">
            <h2 className="font-display text-3xl">HOMEPAGE CONTENT</h2>
            <p className="text-xs text-neutral-500">Every text has an EN and a PT version. Leave PT empty to fall back to EN.</p>

            <Section title="03 — Manifesto" defaultOpen>
                <div className="grid gap-4 sm:grid-cols-3">
                    <BI label="Line 1 (sticky)" value={data.manifesto_line1} onChange={set("manifesto_line1")} />
                    <BI label="Line 2" value={data.manifesto_line2} onChange={set("manifesto_line2")} />
                    <BI label="Line 3" value={data.manifesto_line3} onChange={set("manifesto_line3")} />
                </div>
                <BI label="Text" value={data.manifesto_text} onChange={set("manifesto_text")} rows={3} />
                <BI label="'The + Connects' title" value={data.connects_title} onChange={set("connects_title")} />
            </Section>

            <Section title="05 — Who We Are">
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="Title line 1" value={data.who_title1} onChange={set("who_title1")} />
                    <BI label="Title line 2" value={data.who_title2} onChange={set("who_title2")} />
                </div>
                <BI label="Text" value={data.who_text} onChange={set("who_text")} rows={4} />
                <Img label="Photograph" value={data.who_image} onChange={set("who_image")} />
            </Section>

            <Section title="06 — Our Territory (4 blocks)">
                <BI label="Section title" value={data.territory_title} onChange={set("territory_title")} />
                {(data.territory || []).map((t, i) => (
                    <div key={i} className="space-y-3 border border-neutral-200 p-4">
                        <p className="text-[11px] font-bold tracking-widest text-neutral-400">BLOCK {t.num}</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <BI label="Top line" value={t.title1} onChange={(v) => setArr("territory", i, { title1: v })} />
                            <BI label="Big title" value={t.title2} onChange={(v) => setArr("territory", i, { title2: v })} />
                        </div>
                        <BI label="Text" value={t.text} onChange={(v) => setArr("territory", i, { text: v })} rows={2} />
                        <Img label="Image" value={t.image} onChange={(v) => setArr("territory", i, { image: v })} />
                    </div>
                ))}
            </Section>

            <Section title="03 — Talents">
                <BI label="Overline" value={data.talents_overline} onChange={set("talents_overline")} />
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="Title line 1" value={data.talents_title1} onChange={set("talents_title1")} />
                    <BI label="Title line 2" value={data.talents_title2} onChange={set("talents_title2")} />
                </div>
                <BI label="Introduction text" value={data.talents_text} onChange={set("talents_text")} rows={2} />
            </Section>

            <Section title="07 — Audiences">
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="Title line 1" value={data.audiences_title1} onChange={set("audiences_title1")} />
                    <BI label="Title line 2" value={data.audiences_title2} onChange={set("audiences_title2")} />
                </div>
                {(data.audiences || []).map((a, i) => (
                    <div key={i} className="space-y-3 border border-neutral-200 p-4">
                        <BI label={`Audience ${i + 1} — Title`} value={a.title} onChange={(v) => setArr("audiences", i, { title: v })} />
                        <Img label="Background image" value={a.image} onChange={(v) => setArr("audiences", i, { image: v })} />
                        {(a.points || []).map((p, k) => (
                            <BI key={k} label={`Point ${k + 1}`} value={p} onChange={(v) => setArr("audiences", i, { points: a.points.map((x, j) => (j === k ? v : x)) })} />
                        ))}
                    </div>
                ))}
            </Section>

            <Section title="08 — What We Do (pillars)">
                <BI label="Section title" value={data.pillars_title} onChange={set("pillars_title")} />
                {(data.pillars || []).map((p, i) => (
                    <div key={i} className="space-y-3 border border-neutral-200 p-4">
                        <p className="text-[11px] font-bold tracking-widest text-neutral-400">{p.title}</p>
                        <BI label="Intro text" value={p.text} onChange={(v) => setArr("pillars", i, { text: v })} rows={2} />
                        {(p.items || []).map((item, k) => (
                            <BI key={k} label={`Item ${k + 1}`} value={item} onChange={(v) => setArr("pillars", i, { items: p.items.map((x, j) => (j === k ? v : x)) })} />
                        ))}
                    </div>
                ))}
            </Section>

            <Section title="09 — Creative Thinking">
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="Line 1" value={data.creative_line1} onChange={set("creative_line1")} />
                    <BI label="Line 2" value={data.creative_line2} onChange={set("creative_line2")} />
                    <BI label="Line 3" value={data.creative_line3} onChange={set("creative_line3")} />
                    <BI label="Line 4" value={data.creative_line4} onChange={set("creative_line4")} />
                </div>
                <BI label="Text" value={data.creative_text} onChange={set("creative_text")} rows={3} />
            </Section>

            <Section title="10 / 12 — Projects & People titles">
                <BI label="Projects section title" value={data.projects_title} onChange={set("projects_title")} />
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="People title line 1" value={data.people_title1} onChange={set("people_title1")} />
                    <BI label="People title line 2" value={data.people_title2} onChange={set("people_title2")} />
                </div>
                <BI label="People intro text" value={data.people_text} onChange={set("people_text")} rows={2} />
            </Section>

            <Section title="13 — Philosophy">
                <BI label="Section title" value={data.philosophy_title} onChange={set("philosophy_title")} />
                {(data.philosophy || []).map((p, i) => (
                    <BI key={i} label={`Statement ${p.num}`} value={p.text} onChange={(v) => setArr("philosophy", i, { text: v })} rows={2} />
                ))}
            </Section>

            <Section title="14 / 15 — Final CTA & Contact">
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="CTA line 1" value={data.cta_line1} onChange={set("cta_line1")} />
                    <BI label="CTA line 2" value={data.cta_line2} onChange={set("cta_line2")} />
                    <BI label="CTA line 3" value={data.cta_line3} onChange={set("cta_line3")} />
                    <BI label="CTA line 4" value={data.cta_line4} onChange={set("cta_line4")} />
                </div>
                <BI label="CTA supporting text" value={data.cta_text} onChange={set("cta_text")} rows={2} />
                <BI label="CTA button label" value={data.cta_button} onChange={set("cta_button")} />
                <div className="grid gap-4 sm:grid-cols-2">
                    <BI label="Contact title line 1" value={data.contact_title1} onChange={set("contact_title1")} />
                    <BI label="Contact title line 2" value={data.contact_title2} onChange={set("contact_title2")} />
                </div>
            </Section>

            <SaveBar onSave={save} saving={saving} testId="content-save-btn" />
        </div>
    );
}
